import { onAuthStateChanged } from 'firebase/auth';
import { postCommonRequest } from "./axiosAction";
import { auth, customTokenSignIn } from "../apis/firebase";
import { EnvironmentName, PAGE_LOGIN, PAGE_HOME } from "../constant/systemConstant";
import { Mode } from "../constant/applicationConstant";
import { generateKeyPairRsaOaep, exportPublicKey, arrayBufferToBase64, decryptRsaOaepAndConcatenate } from './cryptoUtils';
const STR_TOKEN = "token";
/**
 * Firebase認証状態の変化を監視します
 * 認証成功時に実行する初期化関数を受け取った場合、初期処理の関数を実行します。
 * 関数が提供されない場合は何も実行しません。
 * @param mode モード名
 * @param onEvent 認証成功もしくは失敗時に実行する関数（省略可能） 指定されない場合は自動的に undefined となる。
 */
export const isLoginFirebase = (mode, onEvent) => {
    // URLを取得
    const urlObj = new URL(window.location.href);
    // URLSearchParamsオブジェクトを取得
    const params = urlObj.searchParams;
    const token = params.get(STR_TOKEN);
    // URLパラメータよりトークン項目を削除する
    removeQueryParamFromUrl(STR_TOKEN);
    // ユーザー認証の状態監視
    onAuthStateChanged(auth, user => {
        if (user) {
            // Firebase Authenticationのログイン成功時
            // console.log("User is signed in:", user);
            // URLパラメータよりトークン項目を削除する
            removeQueryParamFromUrl(STR_TOKEN);
            // メインページの場合ログイン成功時に起動処理を実行する
            if (mode === Mode.MAIN && onEvent)
                onEvent();
            // ログインページの場合ログイン成功時にメインページへリダイレクト
            if (mode === Mode.LOGIN)
                window.location.href = PAGE_HOME;
        }
        else {
            if (process.env.ENVIRONMENT_MODE === EnvironmentName.DEV && token === null) {
                // 開発モードのみログイン画面へ遷移する
                // メインページの場合ログイン失敗時はログインページへリダイレクトする
                if (mode === Mode.MAIN)
                    window.location.href = '/' + PAGE_LOGIN;
                // ログインページの場合ログイン失敗時に起動処理を実行する
                if (mode === Mode.LOGIN && onEvent)
                    onEvent();
            }
            else if (token !== null) {
                callCustomFirebaseAuthentication(token);
            }
            else {
                // Firebase認証されず、かつデジタルツールから遷移せずにトークンがない場合はCookie経由で認証にトライする
                callUserAuthAfterInitialLogin(true, false, false, false);
            }
        }
    });
};
// ログアウト処理
export const logoutFirebase = () => {
    auth.signOut().then(() => {
        console.log('User logged out successfully.');
    }).catch((error) => {
        console.error('Logout failed:', error);
    });
};
/**
 * 指定されたURLパラメータを現在のブラウザのURLから削除します。
 * History APIのreplaceStateを使用して、ページのリロードなしにアドレスバーのURLを更新します。
 *
 * @param parameter {string} - 削除するクエリパラメータのキー。
 */
const removeQueryParamFromUrl = (parameter) => {
    // 現在のウィンドウのURLからURLオブジェクトを作成
    const urlObj = new URL(window.location.href);
    // URLから指定されたクエリパラメータを削除
    urlObj.searchParams.delete(parameter);
    // 変更後のURLを文字列として取得
    const newUrl = urlObj.toString();
    // History APIのreplaceStateメソッドを使って、現在の履歴エントリを新しいURLで置き換える
    window.history.replaceState(null, '', newUrl);
};
// Firebase Authentication API Token取得
export const getFirebaseAuthToken = async (isRefresh = true) => {
    // APIの向き先がWorkstationではない場合
    if (process.env.APP_API_URL?.indexOf("cloudworkstations") === -1) {
        return await auth.currentUser?.getIdToken(/* forceRefresh */ isRefresh);
    }
    // 以下のURLからworkstations環境用のトークンを取得して編集してください。
    // https://cloud.google.com/workstations/docs/authentication?hl=ja#http
    // パラメーター：projects/sej-cloud-workstations/locations/asia-northeast1/workstationClusters/cluster-tokyo/workstationConfigs/apps-stable/workstations/workstation-[固有名]
    // curlで取得する場合
    // curl --request POST "https://workstations.googleapis.com/v1/projects/sej-cloud-workstations/locations/asia-northeast1/workstationClusters/cluster-tokyo/workstationConfigs/apps-stable/workstations/w-kudo:generateAccessToken" --header "Authorization: Bearer $(gcloud auth print-access-token)" --header "Accept: application/json"  --header "Content-Type: application/json"  --data "{}" --compressed | jq
    return "";
};
/**
 * Firebase Authentication API Token取得
 * Firebase Authentication の getIdToken() メソッドは、ユーザーの ID トークンを取得するための標準 API
 * ID トークンには 有効期限 と 自動更新機能 があり、通常の利用では getIdToken() を呼ぶだけで常に最新かつ有効なトークンを安全に取得できる
 * Firebase SDK は メモリ と ローカルストレージ を利用してトークンをキャッシュし、期限切れ前に Firebase SDK が自動的にバックグラウンド更新を行う
 *
 * @param isRefresh
 * @returns
 */
export const getAPIToken = async (isRefresh = false) => {
    return await auth.currentUser?.getIdToken(/* forceRefresh */ isRefresh);
};
// Firebaseユーザー情報取得
export const getFirebaseUserInfo = () => {
    return auth.currentUser !== null ? auth.currentUser : {};
};
// Firebaseログイン判定
export const isFirebaseAuth = () => {
    return auth.currentUser !== null;
};
/**
 * Firebaseカスタム認証及びユーザー情報取得
 * トークン情報の再利用を防ぐために公開鍵方式を用いる
 * @param token デジタルツールにて生成されたワンタイムトークン
 */
const callCustomFirebaseAuthentication = async (token) => {
    try {
        const url = process.env.DIGITAL_TOOLS_URL + "/auth/v1/users/user-auth";
        // Crypto Web APIでの公開鍵方式を用いて暗号化/複合化を行う
        const keyPair = await generateKeyPairRsaOaep();
        const publicKey = await exportPublicKey(keyPair);
        const publicKeyBase64 = arrayBufferToBase64(publicKey);
        const param = {
            "user_token": token,
            "public_key": publicKeyBase64
        };
        const response = await postCommonRequest(url, param);
        const obj = JSON.parse(response.data.data);
        const customToken = await decryptRsaOaepAndConcatenate(obj["custom_token"], keyPair);
        // Firebaseカスタム認証を行う
        customTokenSignIn(auth, customToken)
            .then(() => {
            console.log("signInWithCustomToken custom");
        })
            .catch((e) => {
            console.log(e);
        });
    }
    catch (err) {
        console.error(err);
    }
};
/**
 * 2回目ログイン以降Cookie処理及びアプリケーション情報やユーザー情報取得
 * 一部トークンでは情報の再利用を防ぐために公開鍵方式を用いる
 * @param token デジタルツールにて生成されたワンタイムトークン
 */
const callUserAuthAfterInitialLogin = async (isCookieAuth = false, isCookieUpdate = false, isUserInfo = false, isStorageToken = false) => {
    try {
        const url = process.env.DIGITAL_TOOLS_URL + "/auth/v1/users/user-auth-after-initial-login";
        // Crypto Web APIでの公開鍵方式を用いて暗号化/複合化を行う
        const keyPair = await generateKeyPairRsaOaep();
        const publicKey = await exportPublicKey(keyPair);
        const publicKeyBase64 = arrayBufferToBase64(publicKey);
        const param = {
            "public_key": publicKeyBase64,
            "is_cookie_auth": isCookieAuth,
            "is_cookie_update": isCookieUpdate,
            "is_user_info": isUserInfo,
            "is_storage_token": isStorageToken,
        };
        const response = await postCommonRequest(url, param);
        const obj = JSON.parse(response.data.data);
        if (isCookieAuth) {
            const token = await decryptRsaOaepAndConcatenate(obj["custom_token"], keyPair);
            // Firebaseカスタム認証を行う
            customTokenSignIn(auth, token)
                .then(() => {
                console.log("signInWith Cookie Custom Token");
            })
                .catch((e) => {
                console.log(e);
                // ログイン失敗時はデジタルツールに遷移する
                console.log("no token");
                window.location.href = process.env.DIGITAL_TOOLS_URL;
            });
        }
    }
    catch (err) {
        console.error(err);
        throw err;
    }
};
